import {
  Family as FamilyProto,
  FetchInvoiceHistoryResponse_InvoiceMonth,
} from "protogen/advisors_service_pb";
import { Box, Button, Collapse, Typography } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFetchInvoiceHistory } from "../../services/advisor";
import { pluralize } from "../../common/utils";
import { useLocation } from "react-router-dom";
import Loading from "../common/Loading";
import LinkRouter from "../navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";

const formatMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${remainingMinutes}m`;
  }
};

const formatDateRange = (
  startDate: string,
  endDate: string,
  year: number,
): string => {
  const currentYear = new Date().getFullYear();

  if (year !== currentYear) {
    return `${startDate} - ${endDate}, ${year}`;
  }

  return `${startDate} - ${endDate}`;
};

const InvoiceMonth = ({
  defaultExpanded = false,
  month,
}: {
  defaultExpanded?: boolean;
  month: FetchInvoiceHistoryResponse_InvoiceMonth;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: "1px solid #D4D4D4",
        padding: "20px 16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          cursor: "pointer",
          userSelect: "none",
          alignItems: "center",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
          }}
        >
          <Typography
            variant={isMobile ? "bodyHeavy" : "body"}
            sx={{ flex: "3" }}
          >
            {formatDateRange(
              month.startDate,
              month.endDate,
              new Date(Number(month.endDateSec) * 1000).getFullYear(),
            )}
          </Typography>
          {/*<Typography variant="body" sx={{ flex: "1" }}>*/}
          {/*  ${month.amountCents / 100}*/}
          {/*</Typography>*/}
          <Box
            sx={{
              display: "flex",
              gap: isMobile ? "" : "4px",
              flex: isMobile ? "" : "2",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: isMobile ? "" : "1",
              }}
            >
              <Typography variant={isMobile ? "bodySmall" : "body"}>
                {parseFloat((month.totalMinutes / 60).toFixed(1))} hours
              </Typography>
              {month.serviceHours > 0 && (
                <Typography variant="bodySmall" color="#686868">
                  {parseFloat(month.serviceHours.toFixed(1))} Hours purchased
                </Typography>
              )}
            </Box>
            {isMobile && (
              <Typography variant="bodySmall" sx={{ marginRight: "4px" }}>
                ,{" "}
              </Typography>
            )}
            <Typography
              variant={isMobile ? "bodySmall" : "body"}
              sx={{ flex: "1" }}
            >
              {month.tasksCount} {pluralize("task", month.tasksCount)}
            </Typography>
          </Box>
        </Box>
        {expanded ? (
          <ChevronUp size={24} stroke="#756F6C" cursor="pointer" />
        ) : (
          <ChevronDown size={24} stroke="#756F6C" cursor="pointer" />
        )}
      </Box>
      <Collapse in={expanded}>
        {/* Collapse content container*/}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "12px 0 0 0",
          }}
        >
          {!month.tasks.length && (
            <Typography variant="body">No work recorded</Typography>
          )}
          {/* Task entry item */}
          {month.tasks.map((task) => (
            <Box
              key={task.ref}
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "4px" : "16px",
                borderTop: "1px solid #E2E3E4",
                paddingTop: "16px",
                alignItems: "start",
              }}
            >
              <LinkRouter
                to={`/tasks/${encodeURIComponent(task.ref)}`}
                sx={{
                  display: "flex",
                  flex: "2",
                }}
              >
                <Button
                  variant="text"
                  href=""
                  sx={{
                    height: "auto",
                    textAlign: "start",
                    lineHeight: "1.3",
                  }}
                >
                  {task.title}
                </Button>
              </LinkRouter>

              <Box sx={{ display: "flex", flexDirection: "column", flex: "3" }}>
                {/* Task time records */}
                {task.chunks.map((chunk, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: "8px" }}>
                      <Typography variant="bodyHeavy" sx={{ minWidth: "54px" }}>
                        {formatMinutes(chunk.minutes)}
                      </Typography>
                      <Typography variant="body">
                        {chunk.description || "No description"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="bodySmall"
                      color="#686868"
                      sx={{ textWrap: "nowrap", marginTop: "2px" }}
                    >
                      {chunk.date}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ({ family }: { family: FamilyProto }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { request, data, loading } = useFetchInvoiceHistory();
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    request({
      familyRef: family.ref,
    });
  }, []);
  useEffect(() => {
    if (params.has("currentMonth")) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [params]);
  if (loading) {
    return <Loading />;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }} ref={scrollRef}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3">Monthly summaries</Typography>
          <LinkRouter
            to={`/tools/family-invoices?family=${encodeURIComponent(
              family.ref,
            )}`}
            sx={{ padding: 0 }}
          >
            <Typography variant="bodySmallHeavy" color="primary">
              View invoices
            </Typography>
          </LinkRouter>
        </Box>
        {!data?.invoices?.length && (
          <Typography variant="body">No invoices found</Typography>
        )}
        {(data?.invoices || [])
          .slice()
          .reverse()
          .map((month, i) => (
            <InvoiceMonth
              key={i}
              month={month}
              defaultExpanded={params.has("currentMonth") && i === 0}
            />
          ))}
      </Box>
    </Box>
  );
};
